// SlicePlugin definition for the line slice (PRD-F5 §TR9, §T25/T26).
//
// Lines are connectors — `role: 'connector'` keeps them out of the stack
// panel. The slice declares exactly two capabilities: view-only
// `line-path-strategy` (TR3) and edit-only `updatable-properties` (for
// stroke-width / stroke-color / start-cap / end-cap / pathStrategy).
//
// Bewusst OHNE 'anchor-target' (lines are not targets), 'stack-presence'
// (connector role), 'resize' (lines are edited via endpoint handles, not the
// transformer) und 'rotation', 'text-content', 'rich-edit', 'plain-edit'.
//
// `isAnchorTarget` is intentionally `undefined` — the registry-based
// capability-lookup in `objects/line-anchor.ts` (T6) treats this as "not an
// anchor target" without any string-literal type comparison.
//
// Path-strategy wiring: the slice keeps a module-level cached
// `LineRenderer` produced by `createLineRenderer(registry)` so the
// `createNode/updateNode` signatures match the generic SlicePlugin shape
// without carrying a registry through `SliceRenderContext`. The cached
// renderer is bound the first time `registerStrategies` is called; later
// calls (e.g. a second `<o2d-whiteboard>` element) overwrite the binding.
// Multi-instance support is not in scope for P5.5 — see PRD §D4.

import type { SlicePlugin, StrategyRegistry } from '../../core/object-registry.js';
import {
  type CapabilityDescriptor,
  LINE_PATH_STRATEGY_VIEW,
  UPDATABLE_PROPERTIES_EDIT,
} from '../../core/capabilities/capability.interface.js';
import type Konva from 'konva';
import type { LineObject } from './line.interface.js';
import { createLineDefaults } from './line-defaults.factory.js';
import {
  createLineRenderer,
  type LineRenderer,
} from './line.renderer.js';
import { LinePathStrategyRegistry } from './strategies/line-path-strategy.interface.js';
import { StraightPathStrategy } from './strategies/straight-path-strategy.js';
import { OrthogonalPathStrategy } from './strategies/orthogonal-path-strategy.js';
import { CurvedPathStrategy } from './strategies/curved-path-strategy.js';

const LINE_CAPABILITIES: readonly CapabilityDescriptor[] = [
  { capability: 'line-path-strategy', view: LINE_PATH_STRATEGY_VIEW },
  { capability: 'updatable-properties', edit: UPDATABLE_PROPERTIES_EDIT },
];

let cachedRenderer: LineRenderer | undefined;

function requireRenderer(): LineRenderer {
  if (cachedRenderer === undefined) {
    throw new Error(
      'lineSlice: renderer not initialised — call lineSlice.registerStrategies(registry) before rendering.',
    );
  }
  return cachedRenderer;
}

export const lineSlice: SlicePlugin<LineObject> = {
  type: 'line',
  role: 'connector',
  capabilities: LINE_CAPABILITIES,
  display: {
    iconUrl: 'line',
    label: 'Linie',
  },
  createDefaults: (scale, point, _sizeOpt) => createLineDefaults(scale, point),
  createNode(obj: LineObject, ctx): Konva.Node {
    return requireRenderer().createNode(obj, ctx);
  },
  updateNode(node: Konva.Node, obj: LineObject, ctx): void {
    requireRenderer().updateNode(node, obj, ctx);
  },
  // `isAnchorTarget` is intentionally omitted — lines are connectors, not
  // anchor targets. The registry-based lookup in `line-anchor.ts` treats a
  // missing `isAnchorTarget` as `false`.
  registerStrategies(registry: StrategyRegistry): void {
    // Defensive type-check: a wrong registry would otherwise surface only on
    // the first `register()` call as a confusing runtime error.
    if (!(registry instanceof LinePathStrategyRegistry)) {
      throw new TypeError(
        'lineSlice.registerStrategies expects a LinePathStrategyRegistry',
      );
    }
    registry.register(new StraightPathStrategy());
    registry.register(new OrthogonalPathStrategy());
    registry.register(new CurvedPathStrategy());
    cachedRenderer = createLineRenderer(registry);
  },
};

/**
 * Test-only hook to reset the module-level cached renderer between test
 * cases. Production code MUST NOT call this — `registerStrategies` resets
 * the cache implicitly on every call.
 *
 * @internal
 */
export function __resetLineSliceForTests(): void {
  cachedRenderer = undefined;
}
